import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Galleria } from "primereact/galleria";
import Footer from "./Footer";
import Header from "./Header";
import { URLS } from "../Url";
import axios from "axios";
import { Helmet } from "react-helmet";
import {
  Sparkles,
  ArrowRight,
  MessageCircle,
  Play,
  Pause,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Tv,
} from "lucide-react";

// Curated authentic fallback images from Carnival Castle celebrity & VIP events
const FALLBACK_STAR_MOMENTS = [
  {
    _id: "sm_1",
    image: "uploads/galleryImg/1776006174259-059d6562-b97e-4b7d-93a4-8beab3767fcb.jpg",
    title: "Bride to Be Luxury Suite",
    label: "VIP Exclusive",
    tagline: "Romantic canopy decor with golden fairy lights and celebratory stage.",
  },
  {
    _id: "sm_2",
    image: "uploads/galleryImg/1774802984680-WhatsApp Image 2026-03-29 at 10.19.07 PM.jpeg",
    title: "Star Moments Group Celebration",
    label: "Celebrity Gathering",
    tagline: "Private theatre celebration with neon party lights and VIP friends.",
  },
  {
    _id: "sm_3",
    image: "uploads/galleryImg/1774802474665-WhatsApp Image 2026-03-29 at 10.10.46 PM.jpeg",
    title: "Overhead Theatre Celebration",
    label: "Star Memories",
    tagline: "Surprise party with custom balloon arch and dedicated photography.",
  },
  {
    _id: "sm_4",
    image: "uploads/galleryImg/1773169469457-WhatsApp Image 2026-03-10 at 11.10.41 PM (2) (1).jpeg",
    title: "Star Glasses Birthday Surprise",
    label: "Special Guest",
    tagline: "Fun party props and illuminated celebratory backdrop in private auditorium.",
  },
  {
    _id: "sm_5",
    image: "uploads/galleryImg/1773169212106-WhatsApp Image 2026-03-10 at 11.15.03 PM.jpeg",
    title: "Private Theatre Besties Celebration",
    label: "Star Spotlight",
    tagline: "Intimate gathering with custom celebration cake and 4K cinema projection.",
  },
  {
    _id: "sm_6",
    image: "uploads/galleryImg/1773169199302-WhatsApp Image 2026-03-10 at 11.10.26 PM (4).jpeg",
    title: "24th Birthday LED Lights Affair",
    label: "VIP Milestone",
    tagline: "Custom neon signage and balloon bouquets inside luxury private cinema.",
  },
  {
    _id: "sm_7",
    image: "uploads/galleryImg/1773169189111-WhatsApp Image 2026-03-10 at 11.05.54 PM (4).jpeg",
    title: "Purple Balloon Arch Celebration",
    label: "Celebrity Night",
    tagline: "Signature Carnival Castle purple and gold luxury party styling.",
  },
  {
    _id: "sm_8",
    image: "uploads/galleryImg/1773169151127-WhatsApp Image 2026-03-10 at 10.47.58 PM (4).jpeg",
    title: "Golden Shimmer Birthday Backdrop",
    label: "Gala Backdrop",
    tagline: "Shimmering gold sequin wall with illuminated numerical milestone.",
  },
  {
    _id: "sm_9",
    image: "uploads/galleryImg/1773169134932-WhatsApp Image 2026-03-10 at 11.10.26 PM (3).jpeg",
    title: "Birthday Rose Bouquet Moment",
    label: "Star Surprise",
    tagline: "Fresh premium flower bouquet entry with warm candlelight ambiance.",
  },
  {
    _id: "sm_10",
    image: "uploads/galleryImg/1773169099525-WhatsApp Image 2026-03-10 at 11.05.54 PM (6).jpeg",
    title: "Cool Glasses Birthday Toast",
    label: "Star Celebration",
    tagline: "Unforgettable birthday toast with customized private theatre screen visuals.",
  },
  {
    _id: "sm_11",
    image: "uploads/galleryImg/1773169085827-WhatsApp Image 2026-03-10 at 11.10.26 PM (5).jpeg",
    title: "Candlelit 24th Birthday Party",
    label: "VIP Spotlight",
    tagline: "Ambient pathway candles and LED lettering for milestone moments.",
  },
  {
    _id: "sm_12",
    image: "uploads/galleryImg/1773169051148-WhatsApp Image 2026-03-10 at 10.47.58 PM (2).jpeg",
    title: "Bride to Be Glowing Celebration",
    label: "Star Moments",
    tagline: "Special bachelorette pre-wedding bash in private luxury theatre.",
  },
  {
    _id: "sm_13",
    image: "uploads/galleryImg/1773169040010-WhatsApp Image 2026-03-10 at 10.47.58 PM (5).jpeg",
    title: "Grand Gold & Black Balloon Stage",
    label: "Grand Celebration",
    tagline: "Dramatic gold and black luxury balloon styling with fog machine entry.",
  },
  {
    _id: "sm_14",
    image: "uploads/galleryImg/1773169025555-WhatsApp Image 2026-03-10 at 10.47.58 PM (6).jpeg",
    title: "18th Milestone Birthday Star",
    label: "Star Milestone",
    tagline: "Coming of age party with high-energy private cinema sound and decor.",
  },
  {
    _id: "sm_15",
    image: "uploads/galleryImg/1773168464829-WhatsApp Image 2026-03-10 at 11.06.32 PM (1).jpeg",
    title: "Private Theatre Family Celebration",
    label: "Private Theatre",
    tagline: "Comfortable plush recliners and celebratory family cake cutting.",
  },
  {
    _id: "sm_16",
    image: "uploads/galleryImg/1773168443288-WhatsApp Image 2026-03-10 at 10.48.13 PM.jpeg",
    title: "Bridal Party Theatre Suite",
    label: "Star Moments",
    tagline: "Cozy group moments with delicious food and custom mocktails.",
  },
  {
    _id: "sm_17",
    image: "uploads/galleryImg/1773168395020-WhatsApp Image 2026-03-10 at 11.10.41 PM (2).jpeg",
    title: "Star Glasses Birthday Celebration",
    label: "Star Memories",
    tagline: "Joyful celebratory memories captured by dedicated event staff.",
  },
];

const CYCLE_DURATION_MS = 4200; // 4.2 seconds per spotlight premiere

export default function Gallery() {
  const [images, setImages] = useState(FALLBACK_STAR_MOMENTS);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [activeSpotlightIdx, setActiveSpotlightIdx] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isHovered, setIsHovered] = useState(false);

  const galleria = useRef(null);

  // Responsive column count for the masonry wall
  const [columnCount, setColumnCount] = useState(() => {
    if (typeof window !== "undefined") {
      if (window.innerWidth < 640) return 1;
      if (window.innerWidth < 1024) return 2;
    }
    return 3;
  });

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setColumnCount(1);
      } else if (window.innerWidth < 1024) {
        setColumnCount(2);
      } else {
        setColumnCount(3);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch API images or preserve curated verified collection
  useEffect(() => {
    let isMounted = true;
    axios
      .post(URLS.GetAllGallery, {}, {})
      .then((res) => {
        if (
          isMounted &&
          res.status === 200 &&
          Array.isArray(res.data?.gallerys) &&
          res.data.gallerys.length > 0
        ) {
          const merged = res.data.gallerys.map((item, idx) => {
            const fallback = FALLBACK_STAR_MOMENTS[idx % FALLBACK_STAR_MOMENTS.length];
            return {
              ...item,
              title: item.title || fallback.title,
              label: fallback.label || "Star Moment",
              tagline: fallback.tagline || "Private theatre celebration in Hyderabad.",
            };
          });
          setImages(merged);
        }
      })
      .catch((err) => {
        console.warn("Using local verified Star Moments gallery.", err);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const displayImages = useMemo(() => {
    return images.length > 0 ? images : FALLBACK_STAR_MOMENTS;
  }, [images]);

  // Distribute items across columns so masonry flows with zero empty space
  const columns = useMemo(() => {
    const cols = Array.from({ length: columnCount }, () => []);
    displayImages.forEach((item, index) => {
      cols[index % columnCount].push({
        ...item,
        originalIndex: index,
      });
    });
    return cols;
  }, [displayImages, columnCount]);

  // Automatic Cinema Premiere Spotlight Cycle
  useEffect(() => {
    if (!isPlaying || isHovered) return;

    const timer = setInterval(() => {
      setActiveSpotlightIdx((prev) => (prev + 1) % displayImages.length);
    }, CYCLE_DURATION_MS);

    return () => clearInterval(timer);
  }, [isPlaying, isHovered, displayImages.length]);

  const handleSelectSpotlight = useCallback((idx) => {
    setActiveSpotlightIdx(idx);
  }, []);

  const handlePrevSpotlight = useCallback(() => {
    setActiveSpotlightIdx((prev) => (prev - 1 + displayImages.length) % displayImages.length);
  }, [displayImages.length]);

  const handleNextSpotlight = useCallback(() => {
    setActiveSpotlightIdx((prev) => (prev + 1) % displayImages.length);
  }, [displayImages.length]);

  const togglePlayPause = useCallback(() => {
    setIsPlaying((prev) => !prev);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      if (e.key === "ArrowRight") {
        handleNextSpotlight();
      } else if (e.key === "ArrowLeft") {
        handlePrevSpotlight();
      } else if (e.key === " ") {
        e.preventDefault();
        togglePlayPause();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleNextSpotlight, handlePrevSpotlight, togglePlayPause]);

  const getFullImgUrl = (imgObj) => {
    if (!imgObj || !imgObj.image) return "";
    if (imgObj.image.startsWith("http://") || imgObj.image.startsWith("https://")) {
      return encodeURI(imgObj.image);
    }
    const cleanPath = imgObj.image.startsWith("/") ? imgObj.image.slice(1) : imgObj.image;
    return `https://api.carnivalcastle.com/${encodeURI(cleanPath)}`;
  };

  const itemTemplate = (item) => {
    const src = getFullImgUrl(item);
    return (
      <div className="gal-lightbox-view">
        <img
          src={src}
          alt={item.title || "Carnival Castle Star Moment"}
          className="gal-lightbox-img"
        />
        <div className="gal-lightbox-caption">
          <span className="gal-lb-badge">✦ CELEBRITY MOMENT ✦</span>
          <h4 className="gal-lb-title">{item.title || "Celebrated at Carnival Castle"}</h4>
          <span className="gal-lb-sub">100% Private Theatre Experience • Hyderabad</span>
        </div>
      </div>
    );
  };

  const openLightbox = (index) => {
    setLightboxIndex(index);
    galleria.current?.show();
  };

  const activePhoto = displayImages[activeSpotlightIdx] || displayImages[0];
  const activeImgUrl = getFullImgUrl(activePhoto);

  return (
    <div className="star-page-root">
      <Helmet>
        <meta charSet="utf-8" />
        <title>Star Moments | Celebrity VIP Gallery | Carnival Castle</title>
        <meta
          name="description"
          content="Celebrating the stars who made their moments at Carnival Castle. Explore genuine celebrity celebrations, VIP milestones, and private theatre surprises in Hyderabad."
        />
      </Helmet>

      <Header />

      <main className="star-main">
        {/* ── EDITORIAL HEADER ── */}
        <section className="star-header-sec">
          <div className="star-container">
            <div className="star-header-content">
              <div className="star-eyebrow">
                <Sparkles size={11} className="star-gold-ico" />
                <span>CELEBS ATTENDED</span>
                <Sparkles size={11} className="star-gold-ico" />
              </div>
              <h1 className="star-page-title">
                STAR <span className="star-title-accent">MOMENTS</span>
              </h1>
              <p className="star-page-desc">
                Celebrating the stars who made their moments at Carnival Castle. Immerse in our live VIP Premiere Showcase and explore all 17 celebrity celebrations in full uncropped clarity.
              </p>
              <div className="star-divider">
                <span className="star-div-line" />
                <span className="star-div-star">✦</span>
                <span className="star-div-line" />
              </div>
            </div>
          </div>
        </section>

        {/* ── SECTION 1: THE GRAND CELEBRITY PREMIERE THEATRE STAGE (ZERO BLUR, ZERO EMPTY SPACE) ── */}
        <section className="star-stage-sec" aria-label="Celebrity VIP Premiere Showcase">
          <div className="star-stage-container">
            <div className="star-stage-grid">
              {/* ── LEFT: PURE UNCLUTTERED CELEBRITY MASTERPIECE (ZERO BLUR SPACE) ── */}
              <div
                className="cinema-display-stage"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                <div
                  className="cinema-frame-wrap"
                  key={`frame-${activeSpotlightIdx}`}
                  onClick={() => openLightbox(activeSpotlightIdx)}
                  role="button"
                  tabIndex={0}
                  aria-label={`Open 4K view of ${activePhoto.title}`}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      openLightbox(activeSpotlightIdx);
                    }
                  }}
                >
                  {/* Top Countdown Line directly on the active photo frame */}
                  <div className="cinema-frame-timer">
                    <div
                      key={`timer-${activeSpotlightIdx}-${isPlaying}-${isHovered}`}
                      className={`cinema-frame-timer-bar ${isPlaying && !isHovered ? "is-animating" : "is-paused"
                        }`}
                      style={{ animationDuration: `${CYCLE_DURATION_MS}ms` }}
                    />
                  </div>

                  <img
                    src={activeImgUrl}
                    alt={activePhoto.title}
                    className="cinema-main-photo"
                    onError={(e) => {
                      if (!e.target.dataset.triedLocal && activePhoto?.image) {
                        e.target.dataset.triedLocal = "true";
                        e.target.src = `${URLS.Base}${activePhoto.image}`;
                        return;
                      }
                      e.target.onerror = null;
                      e.target.src = "/img/a1.jpg";
                    }}
                  />

                  {/* Top Badges on Photo */}
                  <div className="cinema-frame-badges">
                    <span className="cinema-live-pill">
                      <span className="cinema-live-dot" />
                      ✦ LIVE PREMIERE
                    </span>
                    <span className="cinema-num-pill">
                      {String(activeSpotlightIdx + 1).padStart(2, "0")} /{" "}
                      {String(displayImages.length).padStart(2, "0")}
                    </span>
                  </div>

                  {/* Hover hint */}
                  <div className="cinema-frame-hover">
                    <span className="cinema-expand-hint">
                      <Maximize2 size={13} />
                      <span>Click for 4K Fullscreen</span>
                    </span>
                  </div>
                </div>
              </div>

              {/* ── RIGHT: THE VIP PREMIERE MARQUEE & MOMENT MATRIX ── */}
              <div className="cinema-details-box">
                {/* Active Photo Headline & Narrative */}
                <div className="cinema-details-header">
                  <div className="cinema-details-tag-row">
                    <span className="cinema-vip-tag">✦ {activePhoto.label || "VIP EXCLUSIVE"}</span>
                    <span className="cinema-theatre-tag">
                      <Tv size={12} />
                      <span>PRIVATE THEATRE HYDERABAD</span>
                    </span>
                  </div>

                  <h2 className="cinema-details-title">{activePhoto.title}</h2>
                  <p className="cinema-details-desc">{activePhoto.tagline}</p>
                </div>

                {/* Player Controls Bar */}
                <div className="cinema-controls-row">
                  <div className="cinema-ctrl-buttons">
                    <button
                      type="button"
                      className="cinema-btn cinema-btn-nav"
                      onClick={handlePrevSpotlight}
                      aria-label="Previous Celebrity Moment (←)"
                      title="Previous Moment"
                    >
                      <ChevronLeft size={16} />
                      <span>Prev</span>
                    </button>

                    <button
                      type="button"
                      className={`cinema-btn cinema-btn-play ${isPlaying ? "is-playing" : "is-paused"
                        }`}
                      onClick={togglePlayPause}
                      aria-label={isPlaying ? "Pause Premiere" : "Play Premiere"}
                      title={isPlaying ? "Pause Premiere (Space)" : "Play Premiere (Space)"}
                    >
                      {isPlaying ? <Pause size={14} /> : <Play size={14} />}
                      <span>{isPlaying ? "Pause" : "Play"}</span>
                    </button>

                    <button
                      type="button"
                      className="cinema-btn cinema-btn-nav"
                      onClick={handleNextSpotlight}
                      aria-label="Next Celebrity Moment (→)"
                      title="Next Moment"
                    >
                      <span>Next</span>
                      <ChevronRight size={16} />
                    </button>
                  </div>

                  <button
                    type="button"
                    className="cinema-btn cinema-btn-expand"
                    onClick={() => openLightbox(activeSpotlightIdx)}
                    title="Launch high-resolution lightbox"
                  >
                    <Maximize2 size={13} />
                    <span>Open 4K</span>
                  </button>
                </div>

                {/* Interactive 17-Moment Thumbnail Matrix (100% Filled, Zero Empty Space) */}
                <div className="cinema-matrix-section">
                  <div className="cinema-matrix-heading">
                    <span>SELECT A CELEBRITY CELEBRATION</span>
                    <span className="cinema-matrix-count">
                      {String(activeSpotlightIdx + 1).padStart(2, "0")} / {displayImages.length}
                    </span>
                  </div>

                  <div className="cinema-matrix-grid">
                    {displayImages.map((thumbItem, thumbIdx) => {
                      const isThumbActive = thumbIdx === activeSpotlightIdx;
                      const thumbSrc = getFullImgUrl(thumbItem);

                      return (
                        <button
                          key={thumbItem._id || thumbIdx}
                          type="button"
                          className={`matrix-tile ${isThumbActive ? "is-active" : ""}`}
                          onClick={() => handleSelectSpotlight(thumbIdx)}
                          title={`Moment ${thumbIdx + 1}: ${thumbItem.title}`}
                          aria-label={`Screen Moment ${thumbIdx + 1}: ${thumbItem.title}`}
                        >
                          <img
                            src={thumbSrc}
                            alt={thumbItem.title}
                            className="matrix-tile-img"
                            onError={(e) => {
                              if (!e.target.dataset.triedLocal && thumbItem?.image) {
                                e.target.dataset.triedLocal = "true";
                                e.target.src = `${URLS.Base}${thumbItem.image}`;
                                return;
                              }
                              e.target.onerror = null;
                              e.target.src = "/img/a1.jpg";
                            }}
                          />
                          <span className="matrix-tile-num">
                            {String(thumbIdx + 1).padStart(2, "0")}
                          </span>
                          {isThumbActive && (
                            <span className="matrix-tile-active-beacon">
                              <span className="matrix-tile-pulse" />
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── SECTION 2: THE COMPLETE CELEBRITY WALL OF FAME (ZERO EMPTY SPACES) ── */}
        <section className="star-wall-sec" aria-label="Celebrity Wall of Fame">
          <div className="star-wall-container">
            <div className="star-wall-heading">
              <div className="star-wall-heading-pill">
                <Sparkles size={11} />
                <span>COMPLETE CELEBRITY ARCHIVE</span>
              </div>
              <h2 className="star-wall-h2">All 17 Star Celebrations in Full Natural Clarity</h2>
              <p className="star-wall-p">
                Every photo displays in its full original proportions with zero cropping and zero empty borders. Click any moment to screen it or inspect in full-resolution 4K.
              </p>
            </div>

            {/* Natural Masonry Flow: Each image fills 100% of its card with ZERO empty space */}
            <div className="star-masonry-wrapper">
              {columns.map((col, colIdx) => (
                <div key={colIdx} className="star-masonry-col">
                  {col.map((item) => {
                    const idx = item.originalIndex;
                    const isSpotlighted = idx === activeSpotlightIdx;
                    const imgSrc = getFullImgUrl(item);

                    return (
                      <article
                        key={item._id || idx}
                        className={`star-card ${isSpotlighted ? "is-spotlighted" : ""}`}
                        onClick={() => {
                          handleSelectSpotlight(idx);
                          openLightbox(idx);
                        }}
                        role="button"
                        tabIndex={0}
                        aria-label={`Star Moment ${idx + 1}: ${item.title}`}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            handleSelectSpotlight(idx);
                            openLightbox(idx);
                          }
                        }}
                      >
                        {/* Edge-to-edge image filling 100% of the card with zero padding */}
                        <div className="star-card-photo-box">
                          <img
                            src={imgSrc}
                            alt={item.title || "Carnival Castle Star Moment"}
                            loading="lazy"
                            className="star-card-full-img"
                            onError={(e) => {
                              if (!e.target.dataset.triedLocal && item?.image) {
                                e.target.dataset.triedLocal = "true";
                                e.target.src = `${URLS.Base}${item.image}`;
                                return;
                              }
                              e.target.onerror = null;
                              const fallbackList = [
                                "/img/a1.jpg",
                                "/img/a2.jpg",
                                "/img/a3.jpg",
                                "/img/a4.jpg",
                                "/img/a5.jpg",
                                "/img/a6.jpg",
                              ];
                              e.target.src = fallbackList[idx % fallbackList.length];
                            }}
                          />

                          {/* Top Badges */}
                          <div className="star-card-badge-row">
                            {isSpotlighted ? (
                              <span className="star-tag-spotlight">
                                <span className="star-beacon-dot" />
                                ✦ NOW ON SCREEN
                              </span>
                            ) : (
                              <span className="star-tag-subtle">
                                ✦ {item.label || "STAR MOMENT"}
                              </span>
                            )}
                            <span className="star-tag-number">
                              #{String(idx + 1).padStart(2, "0")}
                            </span>
                          </div>

                          {/* Seamless Gradient Scrim with Title (Edge-to-Edge) */}
                          <div className="star-card-overlay">
                            <div className="star-card-overlay-content">
                              <span className="star-card-overlay-cat">
                                {item.label || "Celebrity Event"}
                              </span>
                              <h3 className="star-card-overlay-title">{item.title}</h3>
                              <span className="star-card-overlay-cta">
                                <span>Click to Expand</span>
                                <ArrowRight size={12} />
                              </span>
                            </div>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── SECTION 3: BOTTOM CONVERSION & BOOKING BANNER ── */}
        <section className="star-bottom-cta">
          <div className="star-wall-container">
            <div className="star-cta-box">
              <div className="star-cta-text">
                <div className="star-cta-pill">
                  <Sparkles size={11} />
                  <span>PRIVATE THEATRE CELEBRATIONS</span>
                </div>
                <h3 className="star-cta-h3">Celebrate Like a Star at Carnival Castle</h3>
                <p className="star-cta-p">
                  Book an exclusive private auditorium with ambient decor, custom cake, fog entry, and dedicated photography in Hyderabad.
                </p>
              </div>
              <div className="star-cta-actions">
                <a href="/locations" className="star-btn-book">
                  <span>Book Your Star Experience</span>
                  <ArrowRight size={14} />
                </a>
                <a
                  href="https://wa.me/918341428342"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="star-btn-whatsapp"
                >
                  <MessageCircle size={14} />
                  <span>Chat On WhatsApp</span>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* PrimeReact Lightbox Galleria (Full Screen) */}
        <Galleria
          ref={galleria}
          value={displayImages}
          activeIndex={lightboxIndex}
          onItemChange={(e) => setLightboxIndex(e.index)}
          circular
          fullScreen
          showItemNavigators
          showThumbnails={false}
          item={itemTemplate}
        />
      </main>

      <Footer />

      {/* ── BESPOKE LUXURY CINEMA STYLES (ZERO BLUR, ZERO EMPTY SPACES) ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,600;0,700;0,800;1,400&family=DM+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400&display=swap');

        /* ── ROOT & LUXURY CREAM PALETTE ── */
        .star-page-root {
          background-color: #FAF8F5;
          color: #1A0030;
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          position: relative;
          overflow-x: hidden;
          -webkit-font-smoothing: antialiased;
        }

        .star-page-root *, .star-page-root *::before, .star-page-root *::after {
          box-sizing: border-box;
        }

        .star-main {
          flex: 1 0 auto;
        }

        .star-container {
          max-width: 1200px;
          width: calc(100% - 48px);
          margin: 0 auto;
        }

        .star-wall-container {
          max-width: 1380px;
          width: calc(100% - 48px);
          margin: 0 auto;
        }

        @media (max-width: 768px) {
          .star-container, .star-wall-container {
            width: calc(100% - 32px);
          }
        }

        @media (max-width: 480px) {
          .star-container, .star-wall-container {
            width: calc(100% - 24px);
          }
        }

        /* ── EDITORIAL HEADER ── */
        .star-header-sec {
          background: #FAF8F5;
          padding: 44px 20px 24px;
          text-align: center;
          position: relative;
        }

        .star-header-content {
          max-width: 680px;
          margin: 0 auto;
        }

        .star-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          font-size: 10.5px;
          font-weight: 700;
          letter-spacing: 2px;
          text-transform: uppercase;
          color: #B89535;
          background: rgba(201, 168, 76, 0.1);
          border: 1px solid rgba(201, 168, 76, 0.28);
          padding: 5px 14px;
          border-radius: 100px;
          margin-bottom: 14px;
        }

        .star-gold-ico {
          color: #B89535;
        }

        .star-page-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: clamp(28px, 4vw, 42px);
          font-weight: 700;
          color: #1A0030;
          line-height: 1.15;
          letter-spacing: 0.6px;
          margin: 0 0 10px;
          text-transform: uppercase;
        }

        .star-title-accent {
          color: #5E0E73;
          background: linear-gradient(135deg, #1A0030 0%, #5E0E73 50%, #C9A84C 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .star-page-desc {
          font-size: 14.5px;
          line-height: 1.6;
          color: #635770;
          margin: 0 auto 16px;
          max-width: 580px;
          font-weight: 400;
        }

        .star-divider {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
        }

        .star-div-line {
          width: 42px;
          height: 1px;
          background: linear-gradient(to right, transparent, rgba(201, 168, 76, 0.45), transparent);
        }

        .star-div-star {
          color: #B89535;
          font-size: 10px;
        }

        /* ── SECTION 1: THE GRAND CELEBRITY PREMIERE THEATRE STAGE ── */
        .star-stage-sec {
          padding: 10px 0 44px;
        }

        .star-stage-container {
          max-width: 1380px;
          width: calc(100% - 48px);
          margin: 0 auto;
        }

        .star-stage-grid {
          display: grid;
          grid-template-columns: minmax(320px, 460px) 1fr;
          gap: 32px;
          align-items: center;
          background: linear-gradient(135deg, #160029 0%, #26023F 50%, #150027 100%);
          border: 1.5px solid rgba(245, 216, 122, 0.4);
          border-radius: 24px;
          padding: 28px;
          box-shadow: 0 20px 60px rgba(22, 0, 41, 0.28), 0 0 35px rgba(201, 168, 76, 0.15);
        }

        /* ── PURE UNCLUTTERED CELEBRITY MASTERPIECE FRAME (ZERO BLUR, ZERO EMPTY SPACE) ── */
        .cinema-display-stage {
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          min-height: 480px;
          width: 100%;
        }

        .cinema-frame-wrap {
          position: relative;
          display: inline-block;
          max-width: 100%;
          max-height: 490px;
          border-radius: 18px;
          overflow: hidden;
          border: 2.5px solid #F5D87A;
          box-shadow: 0 16px 50px rgba(0, 0, 0, 0.75), 0 0 40px rgba(245, 216, 122, 0.35);
          cursor: pointer;
          animation: cinemaPhotoReveal 0.5s cubic-bezier(0.16, 1, 0.3, 1) both;
          background: #0E0017;
          line-height: 0;
        }

        .cinema-frame-wrap:hover {
          box-shadow: 0 20px 55px rgba(0, 0, 0, 0.85), 0 0 50px rgba(245, 216, 122, 0.5);
        }

        @keyframes cinemaPhotoReveal {
          from {
            opacity: 0.35;
            transform: scale(0.97);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .cinema-main-photo {
          max-width: 100%;
          max-height: 485px;
          width: auto;
          height: auto;
          object-fit: contain;
          display: block;
          transition: transform 0.5s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .cinema-frame-wrap:hover .cinema-main-photo {
          transform: scale(1.02);
        }

        /* Countdown Progress Bar directly on top edge of photo frame */
        .cinema-frame-timer {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3.5px;
          background: rgba(255, 255, 255, 0.2);
          z-index: 15;
          overflow: hidden;
        }

        .cinema-frame-timer-bar {
          height: 100%;
          width: 0%;
          background: linear-gradient(90deg, #F5D87A 0%, #DFBD69 50%, #FFFFFF 100%);
          box-shadow: 0 0 10px rgba(245, 216, 122, 0.95);
        }

        .cinema-frame-timer-bar.is-animating {
          animation: cinemaCountdown 4.2s linear forwards;
        }

        .cinema-frame-timer-bar.is-paused {
          animation-play-state: paused;
        }

        @keyframes cinemaCountdown {
          from { width: 0%; }
          to { width: 100%; }
        }

        /* Badges directly on Photo Frame */
        .cinema-frame-badges {
          position: absolute;
          top: 12px;
          left: 12px;
          right: 12px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          z-index: 6;
          pointer-events: none;
        }

        .cinema-live-pill {
          background: linear-gradient(135deg, rgba(26, 0, 48, 0.92) 0%, rgba(53, 10, 84, 0.92) 100%);
          border: 1px solid #F5D87A;
          color: #F5D87A;
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 1.1px;
          padding: 4px 10px;
          border-radius: 20px;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          box-shadow: 0 3px 12px rgba(245, 216, 122, 0.4);
          backdrop-filter: blur(8px);
          line-height: 1.2;
        }

        .cinema-live-dot {
          width: 7px;
          height: 7px;
          background: #F5D87A;
          border-radius: 50%;
          box-shadow: 0 0 8px #F5D87A;
          animation: starBeaconPulse 1.4s ease-in-out infinite;
        }

        .cinema-num-pill {
          background: rgba(0, 0, 0, 0.7);
          color: rgba(245, 216, 122, 0.95);
          font-size: 9.5px;
          font-weight: 800;
          padding: 4px 9px;
          border-radius: 20px;
          border: 1px solid rgba(245, 216, 122, 0.35);
          backdrop-filter: blur(6px);
          line-height: 1.2;
        }

        .cinema-frame-hover {
          position: absolute;
          bottom: 14px;
          right: 14px;
          z-index: 6;
          opacity: 0;
          transform: translateY(4px);
          transition: all 0.25s ease;
          pointer-events: none;
        }

        .cinema-frame-wrap:hover .cinema-frame-hover {
          opacity: 1;
          transform: translateY(0);
        }

        .cinema-expand-hint {
          background: rgba(250, 248, 245, 0.95);
          color: #1A0030;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 0.5px;
          padding: 6px 12px;
          border-radius: 20px;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          box-shadow: 0 4px 14px rgba(0, 0, 0, 0.5);
          line-height: 1.2;
        }

        /* ── RIGHT: DETAILS & INTERACTIVE MOMENT MATRIX ── */
        .cinema-details-box {
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          background: rgba(14, 0, 23, 0.65);
          border-radius: 18px;
          border: 1px solid rgba(245, 216, 122, 0.25);
          padding: 24px;
        }

        .cinema-details-tag-row {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
          margin-bottom: 10px;
        }

        .cinema-vip-tag {
          font-size: 10.5px;
          font-weight: 800;
          letter-spacing: 1px;
          color: #F5D87A;
          background: rgba(245, 216, 122, 0.12);
          border: 1px solid rgba(245, 216, 122, 0.35);
          padding: 4px 10px;
          border-radius: 20px;
        }

        .cinema-theatre-tag {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.8px;
          color: rgba(255, 255, 255, 0.8);
          display: inline-flex;
          align-items: center;
          gap: 5px;
        }

        .cinema-details-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: clamp(22px, 2.5vw, 30px);
          font-weight: 700;
          color: #FFFFFF;
          margin: 0 0 8px;
          line-height: 1.2;
        }

        .cinema-details-desc {
          font-size: 13.5px;
          line-height: 1.55;
          color: rgba(255, 255, 255, 0.8);
          margin: 0 0 18px;
        }

        /* Controls */
        .cinema-controls-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          flex-wrap: wrap;
          padding: 12px 0 18px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .cinema-ctrl-buttons {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .cinema-btn {
          background: rgba(255, 255, 255, 0.08);
          border: 1px solid rgba(245, 216, 122, 0.3);
          color: #FFFFFF;
          border-radius: 20px;
          padding: 7px 14px;
          font-size: 12px;
          font-weight: 700;
          font-family: inherit;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          cursor: pointer;
          transition: all 0.2s ease;
          line-height: 1;
        }

        .cinema-btn:hover {
          background: rgba(245, 216, 122, 0.2);
          border-color: #F5D87A;
          color: #F5D87A;
          transform: translateY(-1px);
        }

        .cinema-btn-play.is-playing {
          background: linear-gradient(135deg, rgba(201, 168, 76, 0.25) 0%, rgba(94, 14, 115, 0.3) 100%);
          border-color: #F5D87A;
          color: #F5D87A;
        }

        .cinema-btn-expand {
          background: linear-gradient(135deg, #DFBD69 0%, #B89535 100%);
          color: #1A0030;
          border-color: #F5D87A;
          font-weight: 800;
        }

        .cinema-btn-expand:hover {
          background: linear-gradient(135deg, #FFF0B8 0%, #C9A84C 100%);
          color: #1A0030;
        }

        /* 17-Tile Thumbnail Matrix (100% Image Filled, Zero Empty Space) */
        .cinema-matrix-section {
          margin-top: 18px;
        }

        .cinema-matrix-heading {
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-size: 10.5px;
          font-weight: 800;
          letter-spacing: 1.2px;
          color: #F5D87A;
          margin-bottom: 12px;
        }

        .cinema-matrix-count {
          color: rgba(255, 255, 255, 0.6);
        }

        .cinema-matrix-grid {
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          gap: 8px;
        }

        .matrix-tile {
          position: relative;
          aspect-ratio: 16 / 10;
          border-radius: 8px;
          overflow: hidden;
          background: #0E0017;
          border: 1px solid rgba(255, 255, 255, 0.15);
          padding: 0;
          cursor: pointer;
          transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
          outline: none;
        }

        .matrix-tile-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transition: transform 0.3s ease;
        }

        .matrix-tile:hover {
          transform: scale(1.08);
          border-color: rgba(245, 216, 122, 0.7);
          z-index: 5;
        }

        .matrix-tile.is-active {
          border: 2px solid #F5D87A;
          transform: scale(1.08);
          box-shadow: 0 0 16px rgba(245, 216, 122, 0.75);
          z-index: 6;
        }

        .matrix-tile-num {
          position: absolute;
          bottom: 2px;
          right: 3px;
          font-size: 8.5px;
          font-weight: 800;
          color: #FFFFFF;
          background: rgba(0, 0, 0, 0.75);
          padding: 1px 4px;
          border-radius: 4px;
          line-height: 1;
        }

        .matrix-tile-active-beacon {
          position: absolute;
          top: 3px;
          left: 3px;
          width: 6px;
          height: 6px;
          background: #F5D87A;
          border-radius: 50%;
          box-shadow: 0 0 6px #F5D87A;
        }

        .matrix-tile-pulse {
          position: absolute;
          inset: -2px;
          border-radius: 50%;
          border: 1px solid #F5D87A;
          animation: starPulseExpand 1.4s ease-out infinite;
        }

        /* ── SECTION 2: CELEBRITY WALL OF FAME (ZERO EMPTY SPACE) ── */
        .star-wall-sec {
          padding: 44px 0 60px;
        }

        .star-wall-heading {
          text-align: center;
          max-width: 620px;
          margin: 0 auto 34px;
        }

        .star-wall-heading-pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 1.5px;
          color: #B89535;
          text-transform: uppercase;
          background: rgba(201, 168, 76, 0.1);
          border: 1px solid rgba(201, 168, 76, 0.25);
          padding: 4px 12px;
          border-radius: 100px;
          margin-bottom: 10px;
        }

        .star-wall-h2 {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: clamp(24px, 3.2vw, 32px);
          font-weight: 700;
          color: #1A0030;
          margin: 0 0 8px;
        }

        .star-wall-p {
          font-size: 14px;
          color: #635770;
          line-height: 1.55;
          margin: 0;
        }

        /* Multi-Column Masonry: Image wraps tightly with 0 padding */
        .star-masonry-wrapper {
          display: flex;
          gap: 26px;
          width: 100%;
          align-items: flex-start;
        }

        .star-masonry-col {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 26px;
          min-width: 0;
        }

        .star-card {
          position: relative;
          border-radius: 18px;
          overflow: hidden;
          background: #140220;
          border: 1.5px solid rgba(201, 168, 76, 0.2);
          box-shadow: 0 6px 24px rgba(0, 0, 0, 0.08);
          outline: none;
          cursor: pointer;
          transition: transform 0.45s cubic-bezier(0.16, 1, 0.3, 1),
                      box-shadow 0.45s cubic-bezier(0.16, 1, 0.3, 1),
                      border-color 0.35s ease;
        }

        .star-card:hover {
          transform: translateY(-5px);
          border-color: rgba(245, 216, 122, 0.65);
          box-shadow: 0 16px 40px rgba(0, 0, 0, 0.18), 0 0 24px rgba(94, 14, 115, 0.2);
        }

        .star-card.is-spotlighted {
          border-color: #F5D87A;
          box-shadow: 0 0 35px rgba(245, 216, 122, 0.45),
                      0 18px 45px rgba(94, 14, 115, 0.35);
          transform: translateY(-6px);
        }

        /* Photo Box: Image fills 100% of card with zero empty margins */
        .star-card-photo-box {
          position: relative;
          width: 100%;
          overflow: hidden;
          display: block;
        }

        .star-card-full-img {
          width: 100%;
          height: auto;
          display: block;
          object-fit: contain;
          transition: transform 0.55s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .star-card:hover .star-card-full-img {
          transform: scale(1.03);
        }

        /* Badges */
        .star-card-badge-row {
          position: absolute;
          top: 12px;
          left: 12px;
          right: 12px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          z-index: 5;
          pointer-events: none;
        }

        .star-tag-spotlight {
          background: linear-gradient(135deg, rgba(26, 0, 48, 0.92) 0%, rgba(53, 10, 84, 0.92) 100%);
          border: 1px solid #F5D87A;
          color: #F5D87A;
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 1.1px;
          padding: 4px 10px;
          border-radius: 20px;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          box-shadow: 0 3px 12px rgba(245, 216, 122, 0.4);
          backdrop-filter: blur(8px);
        }

        .star-beacon-dot {
          width: 7px;
          height: 7px;
          background: #F5D87A;
          border-radius: 50%;
          box-shadow: 0 0 8px #F5D87A;
          animation: starBeaconPulse 1.4s ease-in-out infinite;
        }

        .star-tag-subtle {
          background: rgba(20, 2, 32, 0.75);
          border: 1px solid rgba(255, 255, 255, 0.22);
          color: rgba(255, 255, 255, 0.92);
          font-size: 9.5px;
          font-weight: 700;
          letter-spacing: 0.8px;
          padding: 3px 9px;
          border-radius: 20px;
          backdrop-filter: blur(6px);
        }

        .star-tag-number {
          background: rgba(0, 0, 0, 0.65);
          color: rgba(245, 216, 122, 0.95);
          font-size: 9.5px;
          font-weight: 800;
          padding: 3px 8px;
          border-radius: 20px;
          border: 1px solid rgba(245, 216, 122, 0.35);
          backdrop-filter: blur(6px);
        }

        /* Bottom Scrim Overlay: Appears naturally on the photo with rich typography */
        .star-card-overlay {
          position: absolute;
          inset: auto 0 0 0;
          background: linear-gradient(to top, rgba(18, 0, 31, 0.92) 0%, rgba(18, 0, 31, 0.6) 65%, transparent 100%);
          padding: 40px 18px 16px;
          z-index: 4;
          transition: background 0.3s ease;
        }

        .star-card-overlay-content {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .star-card-overlay-cat {
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 1px;
          color: #DFBD69;
          text-transform: uppercase;
        }

        .star-card-overlay-title {
          font-family: 'DM Sans', sans-serif;
          font-size: 14.5px;
          font-weight: 700;
          color: #FFFFFF;
          margin: 0;
          line-height: 1.25;
          text-shadow: 0 2px 6px rgba(0, 0, 0, 0.7);
        }

        .star-card-overlay-cta {
          font-size: 11px;
          font-weight: 700;
          color: #F5D87A;
          display: inline-flex;
          align-items: center;
          gap: 5px;
          margin-top: 6px;
          opacity: 0;
          transform: translateY(4px);
          transition: all 0.25s ease;
        }

        .star-card:hover .star-card-overlay-cta {
          opacity: 1;
          transform: translateY(0);
        }

        /* ── LIGHTBOX CUSTOM STYLING ── */
        .gal-lightbox-view {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          max-height: 88vh;
          position: relative;
        }

        .gal-lightbox-img {
          max-width: 90vw;
          max-height: 76vh;
          width: auto;
          height: auto;
          display: block;
          object-fit: contain;
          border-radius: 10px;
          border: 1px solid rgba(201, 168, 76, 0.35);
          box-shadow: 0 14px 55px rgba(0, 0, 0, 0.85);
        }

        .gal-lightbox-caption {
          margin-top: 14px;
          text-align: center;
          color: #FFFFFF;
        }

        .gal-lb-badge {
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 1.5px;
          color: #C9A84C;
          display: inline-block;
          margin-bottom: 4px;
        }

        .gal-lb-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 17px;
          font-weight: 700;
          margin: 0 0 3px;
          color: #FFFFFF;
        }

        .gal-lb-sub {
          font-size: 12px;
          color: rgba(255, 255, 255, 0.7);
        }

        /* ── SECTION 3: BOTTOM CONVERSION CALL TO ACTION ── */
        .star-bottom-cta {
          padding-bottom: 60px;
        }

        .star-cta-box {
          background: linear-gradient(135deg, #1A0030 0%, #350A54 60%, #200438 100%);
          border: 1.5px solid rgba(201, 168, 76, 0.35);
          border-radius: 20px;
          padding: 34px 38px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 22px;
          box-shadow: 0 12px 36px rgba(26, 0, 48, 0.12);
        }

        .star-cta-text {
          max-width: 620px;
        }

        .star-cta-pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 1.5px;
          color: #DFBD69;
          text-transform: uppercase;
          margin-bottom: 8px;
        }

        .star-cta-h3 {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: clamp(22px, 3vw, 28px);
          font-weight: 700;
          color: #FFFFFF;
          margin: 0 0 8px;
        }

        .star-cta-p {
          font-size: 13.5px;
          line-height: 1.55;
          color: rgba(255, 255, 255, 0.82);
          margin: 0;
        }

        .star-cta-actions {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }

        .star-btn-book {
          background: linear-gradient(135deg, #DFBD69 0%, #B89535 100%);
          color: #1A0030;
          font-family: inherit;
          font-size: 13px;
          font-weight: 800;
          letter-spacing: 0.5px;
          padding: 13px 22px;
          border-radius: 12px;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          transition: all 0.22s ease;
          box-shadow: 0 4px 16px rgba(201, 168, 76, 0.35);
        }

        .star-btn-book:hover {
          background: linear-gradient(135deg, #FFF0B8 0%, #C9A84C 100%);
          transform: translateY(-2px);
          box-shadow: 0 6px 22px rgba(201, 168, 76, 0.5);
          color: #1A0030;
        }

        .star-btn-whatsapp {
          background: #25D366;
          color: #FFFFFF;
          font-size: 13px;
          font-weight: 700;
          padding: 13px 20px;
          border-radius: 12px;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 7px;
          transition: all 0.22s ease;
          box-shadow: 0 4px 14px rgba(37, 211, 102, 0.3);
        }

        .star-btn-whatsapp:hover {
          background: #20BA5A;
          transform: translateY(-2px);
          color: #FFFFFF;
        }

        /* ── ANIMATIONS ── */
        @keyframes starBeaconPulse {
          0%, 100% {
            transform: scale(0.85);
            opacity: 0.7;
          }
          50% {
            transform: scale(1.35);
            opacity: 1;
            box-shadow: 0 0 12px #F5D87A;
          }
        }

        @keyframes starPulseExpand {
          0% {
            transform: scale(0.6);
            opacity: 1;
          }
          100% {
            transform: scale(2.2);
            opacity: 0;
          }
        }

        /* ── RESPONSIVE DESIGN ── */
        @media (max-width: 1024px) {
          .star-stage-grid {
            grid-template-columns: 1fr;
            gap: 22px;
            padding: 20px;
          }
          .cinema-display-stage {
            min-height: 400px;
          }
          .cinema-frame-wrap {
            max-height: 430px;
          }
          .cinema-main-photo {
            max-height: 425px;
          }
          .cinema-matrix-grid {
            grid-template-columns: repeat(6, 1fr);
          }
          .star-masonry-wrapper {
            gap: 20px;
          }
          .star-masonry-col {
            gap: 20px;
          }
        }

        @media (max-width: 640px) {
          .star-header-sec {
            padding: 30px 16px 16px;
          }
          .star-stage-container {
            width: calc(100% - 24px);
          }
          .star-stage-grid {
            padding: 16px;
            border-radius: 18px;
            gap: 16px;
          }
          .cinema-display-stage {
            min-height: 320px;
          }
          .cinema-frame-wrap {
            max-height: 350px;
          }
          .cinema-main-photo {
            max-height: 345px;
          }
          .cinema-matrix-grid {
            grid-template-columns: repeat(4, 1fr);
            gap: 6px;
          }
          .cinema-controls-row {
            flex-direction: column;
            align-items: stretch;
            gap: 10px;
          }
          .cinema-ctrl-buttons {
            justify-content: space-between;
            width: 100%;
          }
          .cinema-btn-expand {
            width: 100%;
            justify-content: center;
          }
          .star-masonry-wrapper {
            gap: 16px;
          }
          .star-masonry-col {
            gap: 16px;
          }
          .star-card {
            border-radius: 14px;
          }
          .star-cta-box {
            flex-direction: column;
            align-items: stretch;
            text-align: center;
            padding: 26px 20px;
          }
          .star-cta-actions {
            flex-direction: column;
            width: 100%;
          }
          .star-btn-book, .star-btn-whatsapp {
            width: 100%;
            justify-content: center;
          }
        }

        /* ── ACCESSIBILITY: RESPECT PREFERS-REDUCED-MOTION ── */
        @media (prefers-reduced-motion: reduce) {
          .cinema-frame-timer-bar,
          .cinema-frame-wrap,
          .star-card,
          .star-card-full-img,
          .star-beacon-dot {
            animation: none !important;
            transition: none !important;
            transform: none !important;
          }
        }
      `}</style>
    </div>
  );
}